<template>
  <div class="pgm-notice-write">
    <!-- 페이지 헤더 -->
    <div class="page-header">
      <div class="header-content">
        <v-btn icon variant="text" color="white" @click="goBack" class="mr-2">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <div>
          <h1 class="page-title">공지사항</h1>
          <p class="page-subtitle">{{ isEdit ? '수정하기' : '새 공지 작성' }}</p>
        </div>
      </div>
    </div>

    <!-- 작성 폼 -->
    <v-card class="write-card" elevation="1">
      <v-card-text class="pa-6">
        <v-form ref="formRef" v-model="formValid">
          <!-- 카테고리 -->
          <v-row>
            <v-col cols="12" md="4">
              <v-select
                v-model="form.nc_category"
                :items="categoryOptions"
                item-title="text"
                item-value="value"
                label="카테고리"
                variant="outlined"
                :rules="[v => !!v || '카테고리를 선택해주세요']"
              >
                <template v-slot:item="{ item, props }">
                  <v-list-item v-bind="props">
                    <template v-slot:prepend>
                      <v-icon :color="item.raw.color">{{ item.raw.icon }}</v-icon>
                    </template>
                  </v-list-item>
                </template>
                <template v-slot:selection="{ item }">
                  <v-chip :color="item.raw.color" size="small" variant="flat">
                    <v-icon start size="small">{{ item.raw.icon }}</v-icon>
                    {{ item.raw.text }}
                  </v-chip>
                </template>
              </v-select>
            </v-col>
          </v-row>

          <!-- 제목 -->
          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model="form.nc_title"
                label="제목"
                variant="outlined"
                placeholder="공지사항 제목을 입력하세요"
                :rules="[v => !!v || '제목을 입력해주세요', v => v.length <= 200 || '제목은 200자 이내로 입력해주세요']"
                counter="200"
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- 내용 (WYSIWYG 에디터) -->
          <v-row>
            <v-col cols="12">
              <label class="editor-label">내용</label>
              <div class="editor-container">
                <QuillEditor
                  ref="quillRef"
                  v-model:content="form.nc_content"
                  content-type="html"
                  :options="editorOptions"
                  @ready="onEditorReady"
                />
              </div>
            </v-col>
          </v-row>

          <!-- 첨부파일 -->
          <v-row>
            <v-col cols="12">
              <label class="editor-label">첨부파일</label>
              <v-file-input
                v-model="newFiles"
                label="파일 선택"
                variant="outlined"
                prepend-icon="mdi-attachment"
                multiple
                show-size
                :rules="[v => !v || v.every((f: File) => f.size < 10 * 1024 * 1024) || '파일 크기는 10MB 이하여야 합니다']"
                accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.hwp,.txt,.zip"
                hint="최대 5개, 각 파일 10MB 이하"
                persistent-hint
              ></v-file-input>
              
              <!-- 기존 첨부파일 (수정 시) -->
              <div v-if="existingFiles.length > 0" class="existing-files mt-4">
                <div class="files-header">기존 첨부파일</div>
                <div 
                  v-for="file in existingFiles" 
                  :key="file.nf_idx" 
                  class="file-item"
                >
                  <v-icon size="small" class="mr-2">mdi-file</v-icon>
                  <span class="file-name">{{ file.nf_original_name }}</span>
                  <span class="file-size">({{ formatFileSize(file.nf_size) }})</span>
                  <v-btn 
                    icon 
                    size="small" 
                    variant="text" 
                    color="error"
                    @click="removeExistingFile(file)"
                  >
                    <v-icon size="small">mdi-close</v-icon>
                  </v-btn>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-divider></v-divider>

      <!-- 버튼 -->
      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn variant="outlined" @click="goBack" :disabled="saving">
          취소
        </v-btn>
        <v-btn 
          color="primary" 
          variant="flat" 
          @click="saveNotice"
          :loading="saving"
          :disabled="!formValid"
        >
          <v-icon start>mdi-check</v-icon>
          {{ isEdit ? '수정' : '등록' }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- 스낵바 -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="3000"
      location="top"
    >
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axios from 'axios';
import { QuillEditor } from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';

interface NoticeFile {
  nf_idx: number;
  nf_original_name: string;
  nf_size: number;
}

const route = useRoute();
const router = useRouter();

const formRef = ref();
const quillRef = ref();
const formValid = ref(false);
const saving = ref(false);
const snackbar = ref({ show: false, text: '', color: 'info' });

// 폼 데이터
const form = ref({
  nc_category: 'NOTICE' as 'NOTICE' | 'EVENT' | 'URGENT',
  nc_title: '',
  nc_content: ''
});

const newFiles = ref<File[]>([]);
const existingFiles = ref<NoticeFile[]>([]);
const filesToDelete = ref<number[]>([]);

// 수정 모드 여부
const isEdit = computed(() => !!route.params.ncIdx);

// 카테고리 옵션 (센터장은 UPDATE 제외)
const categoryOptions = [
  { text: '공지사항', value: 'NOTICE', icon: 'mdi-bullhorn', color: 'primary' },
  { text: '이벤트', value: 'EVENT', icon: 'mdi-party-popper', color: 'success' },
  { text: '긴급', value: 'URGENT', icon: 'mdi-alert', color: 'error' }
];

// Quill 에디터 옵션
const editorOptions = {
  theme: 'snow',
  placeholder: '공지사항 내용을 입력하세요...',
  modules: {
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        [{ 'size': ['small', false, 'large', 'huge'] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'align': [] }],
        ['link', 'image'],
        ['clean']
      ],
      handlers: {
        image: imageHandler
      }
    }
  }
};

// 이미지 핸들러
function imageHandler() {
  const input = document.createElement('input');
  input.setAttribute('type', 'file');
  input.setAttribute('accept', 'image/*');
  input.click();
  
  input.onchange = async () => {
    const file = input.files?.[0];
    if (!file) return;
    
    // 파일 크기 체크 (5MB)
    if (file.size > 5 * 1024 * 1024) {
      showSnackbar('이미지 크기는 5MB 이하여야 합니다.', 'error');
      return;
    }
    
    const formData = new FormData();
    formData.append('image', file);
    
    try {
      const response = await axios.post('/api/notice/upload-image', formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        const quill = quillRef.value?.getQuill();
        if (quill) {
          const range = quill.getSelection(true);
          quill.insertEmbed(range.index, 'image', response.data.url);
          quill.setSelection(range.index + 1);
        }
      }
    } catch (error) {
      console.error('이미지 업로드 오류:', error);
      showSnackbar('이미지 업로드에 실패했습니다.', 'error');
    }
  };
}

// 에디터 준비 완료
const onEditorReady = () => {
  // 필요한 초기화
};

// 기존 공지 로드 (수정 시)
const loadNotice = async () => {
  const ncIdx = route.params.ncIdx;
  if (!ncIdx) return;
  
  try {
    const response = await axios.get(`/api/notice/detail/${ncIdx}`, {
      params: { increaseHit: 'false' },
      withCredentials: true
    });
    
    if (response.data.success) {
      const notice = response.data.notice;
      form.value.nc_category = notice.nc_category || 'NOTICE';
      form.value.nc_title = notice.nc_title;
      form.value.nc_content = notice.nc_content;
      existingFiles.value = response.data.files || [];
    }
  } catch (error) {
    console.error('공지사항 로드 오류:', error);
    showSnackbar('공지사항을 불러오는데 실패했습니다.', 'error');
  }
};

// 저장
const saveNotice = async () => {
  if (!formRef.value?.validate()) return;
  
  saving.value = true;
  
  try {
    let ncIdx: number;
    
    // 센터장은 항상 CENTER 타입
    const nc_type = 'CENTER';
    
    if (isEdit.value) {
      // 수정
      const response = await axios.put(`/api/notice/update/${route.params.ncIdx}`, {
        nc_type: nc_type,
        nc_category: form.value.nc_category,
        nc_title: form.value.nc_title,
        nc_content: form.value.nc_content
      }, { withCredentials: true });
      
      if (!response.data.success) {
        throw new Error(response.data.message);
      }
      
      ncIdx = parseInt(route.params.ncIdx as string);
      
      // 삭제할 파일 처리
      for (const nfIdx of filesToDelete.value) {
        await axios.delete(`/api/notice/file/${nfIdx}`, { withCredentials: true });
      }
    } else {
      // 등록
      const response = await axios.post('/api/notice/create', {
        nc_type: nc_type,
        nc_category: form.value.nc_category,
        nc_title: form.value.nc_title,
        nc_content: form.value.nc_content
      }, { withCredentials: true });
      
      if (!response.data.success) {
        throw new Error(response.data.message);
      }
      
      ncIdx = response.data.insertId;
    }
    
    // 새 첨부파일 업로드
    if (newFiles.value && newFiles.value.length > 0) {
      const formData = new FormData();
      newFiles.value.forEach(file => {
        formData.append('files', file);
      });
      
      await axios.post(`/api/notice/upload/${ncIdx}`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    }
    
    showSnackbar(isEdit.value ? '공지사항이 수정되었습니다.' : '공지사항이 등록되었습니다.', 'success');
    
    setTimeout(() => {
      router.push(`/pgm/notice/${ncIdx}`);
    }, 1000);
    
  } catch (error: any) {
    console.error('저장 오류:', error);
    showSnackbar(error.message || '저장에 실패했습니다.', 'error');
  } finally {
    saving.value = false;
  }
};

// 기존 파일 삭제 표시
const removeExistingFile = (file: NoticeFile) => {
  existingFiles.value = existingFiles.value.filter(f => f.nf_idx !== file.nf_idx);
  filesToDelete.value.push(file.nf_idx);
};

// 파일 크기 포맷
const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

// 네비게이션
const goBack = () => {
  router.push('/pgm/notice');
};

const showSnackbar = (text: string, color: string = 'info') => {
  snackbar.value = { show: true, text, color };
};

onMounted(() => {
  if (isEdit.value) {
    loadNotice();
  }
});
</script>

<style lang="scss" scoped>
.pgm-notice-write {
  padding: 16px;
  max-width: 900px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 16px 24px;
  background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
  border-radius: 12px;
  color: white;
  
  .header-content {
    display: flex;
    align-items: center;
  }
  
  .page-title {
    font-size: 20px;
    font-weight: 700;
    margin: 0;
  }
  
  .page-subtitle {
    font-size: 13px;
    opacity: 0.8;
    margin: 0;
  }
}

.write-card {
  border-radius: 12px;
}

.editor-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 8px;
}

.editor-container {
  border: 1px solid #ccc;
  border-radius: 8px;
  overflow: hidden;
  
  :deep(.ql-toolbar) {
    border: none;
    border-bottom: 1px solid #ccc;
    background: #fafafa;
  }
  
  :deep(.ql-container) {
    border: none;
    min-height: 300px;
    font-size: 15px;
  }
  
  :deep(.ql-editor) {
    min-height: 300px;
    line-height: 1.8;
    
    img {
      max-width: 100%;
      height: auto;
    }
  }
}

.existing-files {
  .files-header {
    font-size: 14px;
    font-weight: 500;
    color: #666;
    margin-bottom: 8px;
  }
  
  .file-item {
    display: flex;
    align-items: center;
    padding: 8px 12px;
    background: #f5f5f5;
    border-radius: 8px;
    margin-bottom: 8px;
    
    .file-name {
      font-size: 14px;
      color: #333;
    }
    
    .file-size {
      font-size: 12px;
      color: #999;
      margin-left: 8px;
      margin-right: auto;
    }
  }
}
</style>


